import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, ViewChild, ViewContainerRef, inject, computed } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { DataExplorerComponent } from './data-explorer/data-explorer.component';
import { CommonService } from '../../services/common.service';
import { ApiCacheService } from '../../services/api-cache.service';
import { RightSidebarComponent } from './right-sidebar/right-sidebar.component';
import { LabelService } from '../../services/label.service';
import { Dashboard, LabelTypes } from '../../shared/label.types';
import { ConversationOverviewComponent } from './conversation-overview/conversation-overview.component';
import * as Types from '../../shared/types';
import { TabularViewComponent } from './tabular-view/tabular-view.component';
import { DaterangePickerComponent } from '../../components/date-range-picker/date-range-picker.component';
import { FilterSectionComponent } from '../../components/filter-section/filter-section.component';
import { SharedDataService } from '../../services/shared-data.service';
import { transformColumnPreferences, transformTestToFilters } from '../../shared/appUtil';
import { FilterService } from '../../services/filter.service';
import { MoreDropDownComponent } from './more-drop-down/more-drop-down.component';
import { ManageColumnsComponent } from '../../components/manage-columns/manage-columns.component';
import { SearchDropdownComponent } from './search-dropdown/search-dropdown.component';
import { DashboardOptions } from '../../shared/models/dashboard.types';
import { BottomSheetDataService } from '../../services/bottom-sheet-data.service';
import { ReportComponent } from '../../components/report/report.component';
import { TimezoneComponent } from '../../components/timezone/timezone.component';
import { NotificationService } from '../../services/notification.service';
import { CreateDatasetDialogComponent } from '../../components/create-dataset-dialog/create-dataset-dialog.component';
import { ConfirmationOverlayComponent } from '../../components/confirmation-overlay/confirmation-overlay.component';
import dayjs from 'dayjs';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    DataExplorerComponent,
    RightSidebarComponent,
    ConversationOverviewComponent,
    TabularViewComponent,
    FilterSectionComponent,
    MoreDropDownComponent,
    ManageColumnsComponent,
    SearchDropdownComponent,
    TimezoneComponent,
    MatDialogModule,
  ],
  providers: [CommonService],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent implements OnInit {
  private _bottomSheetDataService = inject(BottomSheetDataService);

  constructor(
    public commonService: CommonService,
    public labelService: LabelService,
    public sharedDataService: SharedDataService,
    private filterService: FilterService,
    public notificationService: NotificationService,
    private dialog: MatDialog,
    private apiCacheService: ApiCacheService
  ) {}

  @ViewChild(TabularViewComponent) tableComponent!: TabularViewComponent;
  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;

  isChatBoxVisible: boolean = false;
  dashboard: Dashboard = {} as Dashboard;
  activeTab: string = 'visualization'; // Default active tab
  protected _tableViewFlag = computed<boolean>(() => this.sharedDataService.tableViewFlagStatus());

  columnLoading: boolean = false;
  tableViewBody: Types.TableViewBody = {
    filters: [],
  } as Types.TableViewBody;
  content: Array<{ label: string; value: string }> = [
    { label: 'option1', value: 'option1' },
    { label: 'option2', value: 'option2' },
    { label: 'option3', value: 'option3' },
  ];
  selectedValue = { value: 'contains', label: 'contains' };
  buttonTitle: string = 'Hide Fields';
  private currentChildComponentRef: any;
  private subscription: Subscription | undefined;
  private overviewFetchSubscription: Subscription | undefined;
  /** Used to ignore stale overview API responses when requests are triggered in quick succession */
  private overviewRequestId = 0;
  /** Flag to prevent duplicate overview calls when multiple triggers fire simultaneously */
  private isFetchingOverview = false;
  /** Subject for debouncing overview fetch calls to prevent duplicate API calls */
  private overviewFetchTrigger$ = new Subject<void>();

  ngOnInit() {
    // Force chart view as default before loading any saved state
    this.sharedDataService.setTableViewFlagStatus(false);
    this.activeTab = 'visualization';

    // Set up debounced overview fetch to prevent duplicate API calls
    this.overviewFetchSubscription = this.overviewFetchTrigger$.pipe(
      debounceTime(300) // Wait 300ms for multiple triggers to settle
    ).subscribe(() => {
      this.performFetchOverview();
    });

    this.labelService.getLabelData().subscribe((data: LabelTypes) => {
      this.dashboard = data?.dashboard ?? {};
    });

    this.filterService.getRefreshDataStatus().subscribe((status) => {
      if (status) {
        this.tableViewBody.filters = this.populateFilteredData();
        this.fetchOverview();
        this.sharedDataService.updateDashboardState('filters', this.filterService.getFilterConditionDataValue());
      }
    });

    this.sharedDataService.filterOnly().subscribe((value) => {
      if (!value) {
        this.initializeChild();
      }
    });

    this.subscription = this.sharedDataService.getDateRange().subscribe((date) => {
      this.tableViewBody.start_date = date.startAPIDate;
      this.tableViewBody.end_date = date.endAPIDate;
      this.tableViewBody.timeZone = this.sharedDataService.selectedTimezoneValue;

      // Call overview whenever we have valid dates (including when navigating back to this dashboard)
      if (date?.startAPIDate && date?.endAPIDate) {
        this.fetchOverview();
      }
    });

    // Listen for timezone changes and trigger conversation overview API call
    this.sharedDataService.getTimezoneChange().subscribe((timezone) => {
      this.tableViewBody.timeZone = timezone;

      // Only trigger API call if we have valid date range
      if (this.tableViewBody.start_date && this.tableViewBody.end_date) {
        this.fetchOverview();
      }
    });

    this.sharedDataService.getDashboardState().subscribe((dashboardData) => {
      // Only override default if there's a valid saved state
      if (dashboardData.tableViewFlag !== undefined && dashboardData.tableViewFlag !== null) {
        this.sharedDataService.setTableViewFlagStatus(!!dashboardData.tableViewFlag);
        this.activeTab = dashboardData.tableViewFlag ? 'tabular' : 'visualization';
      }

      if (dashboardData.filters.length) {
        this.filterService.updateFilterData(dashboardData.filters);
      }

      // When navigating from another tab (e.g. Taxonomy Builder), restore date range from state
      // and fetch overview so the overview panel is populated without waiting for the date picker
      const dr = dashboardData?.dateRange;
      if (dr?.startDate != null && dr?.endDate != null) {
        const start = dayjs(dr.startDate);
        const end = dayjs(dr.endDate);
        if (start.isValid() && end.isValid()) {
          this.tableViewBody.start_date = start.format('YYYY-MM-DD');
          this.tableViewBody.end_date = end.format('YYYY-MM-DD');
          this.tableViewBody.timeZone = this.sharedDataService.selectedTimezoneValue;
          this.fetchOverview();
        }
      }
    });
    this.fetchColumnNames();
    this.fetchFilters();
  }

  ngAfterViewInit() {
    this.initializeChild();
    // When navigating from another tab (e.g. Taxonomy Builder), the date picker sets the date
    // after this view is ready. Ensure we fetch overview once the date is available.
    setTimeout(() => {
      if (this.tableViewBody?.start_date && this.tableViewBody?.end_date) {
        this.fetchOverview();
      }
    }, 100);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.overviewFetchSubscription) {
      this.overviewFetchSubscription.unsubscribe();
    }
  }

  initializeChild() {
    // Destroy the previous component if it exists
    if (this.currentChildComponentRef) {
      this.currentChildComponentRef.destroy();
    }

    this.currentChildComponentRef = this.container?.createComponent(DaterangePickerComponent);
  }

  //download table data csv file
  onDownload() {
    if (this._tableViewFlag()) {
      this.tableComponent.exportPlot();
    }
  }

  viewSavedFilters() {}

  hideInsightsOverview() {}

  fetchColumnNames = async () => {
    this.columnLoading = true;
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        this.sharedDataService.setColumnNames(transformColumnPreferences(names));
        this.columnLoading = false;
      },
      error: () => {
        this.columnLoading = false;
      },
    });
  };

  performFetchOverview = () => {
    // Prevent duplicate calls when already fetching
    if (this.isFetchingOverview) {
      return;
    }

    this.tableViewBody.filters = this.populateFilteredData();

    // Check if we have cached data for this payload
    const cacheKey = 'overview_data';
    const hasCache = this.apiCacheService.hasValidCache(cacheKey, this.tableViewBody);
    const cachedData = this.apiCacheService.getCache(cacheKey, this.tableViewBody);

    if (cachedData) {
      this.processCachedOverviewData(cachedData);
      return;
    }

    this.isFetchingOverview = true;
    const requestId = ++this.overviewRequestId;
    this.commonService.setLoaderFlag(true);
    this.commonService.fetchOverview(this.tableViewBody).subscribe({
      next: (data: Types.Overview) => {
        // Ignore response if a newer overview request was already triggered (e.g. slow API, user changed date/filter again)
        if (requestId !== this.overviewRequestId) {
          this.commonService.setLoaderFlag(false);
          this.isFetchingOverview = false;
          return;
        }
        // Cache the response
        this.apiCacheService.setCache(cacheKey, this.tableViewBody, data);

        this.sharedDataService.setConversationOverview(data);
        this.sharedDataService.totalConversations = data['Total Conversations'];
        this.commonService.setLoaderFlag(false);
        this.filterService.setRefreshDataStatus(false);
        this.isFetchingOverview = false;
      },
      error: (error) => {
        if (requestId === this.overviewRequestId) {
          this.commonService.setLoaderFlag(false);
          this.filterService.setRefreshDataStatus(false);
          this.isFetchingOverview = false;
        }
        console.error('API Error:', error);
      },
    });
  };

  /**
   * Debounced trigger for fetching overview data.
   * This prevents multiple API calls when filters/date range change rapidly.
   */
  fetchOverview = () => {
    this.overviewFetchTrigger$.next();
  };

  /**
   * Process cached overview data
   */
  private processCachedOverviewData(cachedData: Types.Overview): void {
    this.sharedDataService.setConversationOverview(cachedData);
    this.sharedDataService.totalConversations = cachedData['Total Conversations'];
    this.commonService.setLoaderFlag(false);
    this.filterService.setRefreshDataStatus(false);
    this.isFetchingOverview = false;
  }

  /**
   * Clear overview cache (useful for debugging or when cache needs to be invalidated)
   */
  clearOverviewCache(): void {
    this.apiCacheService.clearEndpointCache('overview_data');
  }

  populateFilteredData(): Types.FilterRequest[] {
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data, error } = transformTestToFilters(filterDataArray);
    const filteredData: Types.FilterRequest[] = [...data];
    if (error) {
      // this.notificationService.error('Error in filters', 'Error in filters');
    }
    return filteredData;
  }

  fetchFilters = () => {
    this.commonService.fetchDataFiltersInsights({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });

    this.commonService.fetchDataFiltersClusters({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });

    this.commonService.fetchDataFiltersConnectAttributes({}).subscribe({
      next: (data: Types.DataFilters) => {
        this.filterService.updateAllFilters(data);
      },
      error: (error) => {
        console.error('Error fetching all filters', error);
      },
    });
  };

  setSelectedDomain(filteredData: any[]) {
    const domainFilter = filteredData.find((item) => item.columnName === 'Domain');

    if (domainFilter) {
      // If 'Domain' column is found, set selectedDomain to its value
      this.commonService.selectedDomain = domainFilter.value;
    } else {
      // If 'Domain' column is not found, set selectedDomain to an empty string
      this.commonService.selectedDomain = '';
    }
  }

  handleItemSelect = (value: { label: string; value: string }) => {
    this.selectedValue = value;
  };

  onValueChange(newValue: any) {
    this.isChatBoxVisible = newValue;
  }

  showTab(tab: string): void {
    this.activeTab = tab;
  }

  showChartView(): void {
    // Check if there are selected rows in data view
    if (this._tableViewFlag() && this.hasSelectedRows()) {
      // Show confirmation dialog
      const dialogRef = this.dialog.open(ConfirmationOverlayComponent, {
        width: '480px',
        data: {},
        panelClass: 'confirm-dialog',
        hasBackdrop: false, // We'll handle the backdrop ourselves
        disableClose: true // Prevent closing on backdrop click or escape
      });

      // Set the dialog data
      const dialogInstance = dialogRef.componentInstance;
      dialogInstance.title = 'Switch to Chart View?';
      dialogInstance.message = "You've selected records in Data View. Switching to Chart View will clear your selection. Do you want to continue?";
      dialogInstance.disableYesButton = false;
      dialogInstance.showOnlyOkButton = false;

      dialogRef.componentInstance.confirm.subscribe(() => {
        // User confirmed, switch to chart view and clear selections
        this.sharedDataService.setTableViewFlagStatus(false);
        this.clearSelectedRows();
        // Update the active tab to chart view only after confirmation
        this.activeTab = 'visualization';
        dialogRef.close();
      });

      dialogRef.componentInstance.cancel.subscribe(() => {
        // User cancelled, ensure data view tab shows active styling
        this.activeTab = 'tabular';
        dialogRef.close();
      });
    } else if (this._tableViewFlag()) {
      // No selected rows, switch directly to chart view
      this.sharedDataService.setTableViewFlagStatus(false);
      // Update the active tab to chart view
      this.activeTab = 'visualization';
    }
  }

  showDataView(): void {
    if (!this._tableViewFlag()) {
      this.sharedDataService.setTableViewFlagStatus(true);
      // Update the active tab to data view
      this.activeTab = 'tabular';
    }
  }

  toggleDropdown() {}

  private onCustomerReportOptionClick() {
    this.filterService.checkCustomerEndpointSelected();
    this._bottomSheetDataService.setBottomSheetUiConfig({
      title: 'Customer Report',
      contentComponent: ReportComponent,
    });
    setTimeout(() => {
      this._bottomSheetDataService.setBottomSheetExpandedState('maximized');
    }, 100);
  }

  protected onDashboardOptionsClick(option: DashboardOptions) {
    switch (option) {
      case 'download_csv': {
        this.onDownload();
        return;
      }

      case 'customer_report': {
        this.onCustomerReportOptionClick();
        return;
      }

      default:
        return;
    }
  }

  // Check if there are selected rows in the tabular view
  hasSelectedRows(): boolean {
    return this.tableComponent?.hasSelections() || false;
  }

  getSelectedContactIds(): string[] {
    return this.tableComponent?.getSelectedContactIds() || [];
  }

  openInPlayground(): void {
    const selectedContactIds = this.getSelectedContactIds();
    this.openCreateDatasetDialog(selectedContactIds);
  }

  openCreateDatasetDialog(selectedContactIds: string[]): void {
    // Get the button element to calculate its position
    const button = document.querySelector('.playground-btn-header') as HTMLElement;
    let topPosition = '220px';
    let rightPosition = '95px';

    if (button) {
      const rect = button.getBoundingClientRect();
      const buttonBottom = rect.bottom + window.scrollY;
      const buttonRight = window.innerWidth - rect.right;

      topPosition = `${buttonBottom + 8}px`; // 8px gap below button
      rightPosition = `${buttonRight}px`;
    }

    const dialogRef = this.dialog.open(CreateDatasetDialogComponent, {
      width: '280px',
      data: {
        selectedContactIds: selectedContactIds
      },
      panelClass: 'create-dataset-dialog',
      position: {
        top: topPosition,
        right: rightPosition
      },
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Clear selected rows after successful dataset creation
        this.clearSelectedRows();
      }
    });
  }

  clearSelectedRows(): void {
    if (this.tableComponent) {
      this.tableComponent.clearAllSelections();
    }
  }
}
